import React, { FC, Fragment, useMemo } from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { AsH1, TitleLG } from '@/atoms/Text'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { paths } from '@/constants/paths'
import { LandscapeRowCollection, LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import { PlaceholderMediaCard } from '@/molecules/MediaCard'
import { createWebClient } from '@/services/ApolloClient'
import { LinkViewModel } from '@/services/RenderService'
import { useUser } from '@/services/UserService'
import { useGetDefaultWatchlist, WatchlistItem } from '@/services/WatchlistService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { AnalyticsEvent } from '@/utils/analytics'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'

export const WatchlistCollection: FC = () => {
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const { userId } = useUser()
  // TODO: when web implements user profiles, use that profile id here instead.
  const viewerProfileId = userId ?? undefined

  const client = useMemo(() => {
    return createWebClient({ locale, viewerProfileId })
  }, [locale, viewerProfileId])

  const { defaultWatchlist, loading } = useGetDefaultWatchlist({ client, viewerProfileId })

  if (!loading && defaultWatchlist.length === 0) {
    return null
  }

  return (
    <PaddedContainer className="mb-12 overflow-x-hidden" fluid>
      <div data-testid="your-watchlist-collection" className="relative">
        <header>
          <TitleLG weight="bold" as={AsH1} data-testid="continue-watching-header" className="mb-3 md:mb-4">
            {t('yourWatchlist', 'Your Watchlist')}
          </TitleLG>
        </header>
        {/* We get the User's Watchlist client side so handle hydration here */}
        <LazyHydrate whenVisible>
          <LandscapeRowCollection>
            {loading
              ? Array(5)
                  .fill(0)
                  .map((v, i) => (
                    <PlaceholderMediaCard
                      className="mr-4"
                      borderRadiusClassName="rounded-lg"
                      hideTextPlaceholders
                      key={i}
                    />
                  ))
              : defaultWatchlist.map((watchlistItem) => {
                  const linkModel: LinkViewModel | null = buildLinkModel(watchlistItem)

                  if (!linkModel || !watchlistItem?.project?.slug) return null

                  const title = formatTitle(watchlistItem, t)

                  return (
                    <Fragment key={watchlistItem?.id}>
                      <LandscapeTileCard className="mb-2 mr-4" linkModel={linkModel} key={watchlistItem?.id} />
                      <TitleAndSubtitleDisplay className="mr-4" title={title} subtitle={watchlistItem?.name ?? ''} />
                    </Fragment>
                  )
                })}
          </LandscapeRowCollection>
        </LazyHydrate>
      </div>
    </PaddedContainer>
  )
}

function buildLinkModel(watchlistItem: WatchlistItem): LinkViewModel | null {
  if (
    !watchlistItem?.id ||
    !watchlistItem?.project?.slug ||
    !watchlistItem?.name ||
    !watchlistItem?.imageLandscape?.cloudinaryPath
  )
    return null

  return {
    id: watchlistItem.id,
    linkUrl: `${paths.watch.index}/${watchlistItem.project.slug}`,
    imageUrl: getCloudinaryImageUrl({
      path: watchlistItem?.imageLandscape?.cloudinaryPath ?? '',
    }),
    logoUrl: '',
    label: watchlistItem.name ?? undefined,
    name: watchlistItem.name ?? undefined,
    projectSlug: watchlistItem.project.slug,
    track: {
      eventName: 'Clicked Watchlist Item' as AnalyticsEvent,
      payload: {
        program: watchlistItem.id,
        title: watchlistItem?.name,
        subtitle: watchlistItem?.name,
      },
    },
  }
}

function formatTitle(watchlistItem: WatchlistItem, t: TranslateFunction) {
  if (!watchlistItem) return undefined

  if (watchlistItem.name?.toLowerCase() === 'minisode') {
    return t('minisode', 'Minisode')
  } else if (watchlistItem?.__typename === 'ContentSeries') {
    return t('seriesDescription', 'series')
  } else if (watchlistItem?.__typename === 'ContentSpecial') {
    return t('special', 'Special')
  } else if (watchlistItem?.__typename === 'ContentMovie') {
    return t('film', 'Film')
  }
}
